import { useState, useEffect } from 'react';

/**
 * ConsentBanner — GDPR cookie/data consent banner
 *
 * Shows as a bottom banner on first visit (not a page).
 * Choices: Accept all / Reject / Manage preferences
 * Consent state stored in localStorage and sent with API calls.
 * Syncs with Profile page consent checkbox.
 *
 * GDPR SCD audit trail: every consent change is logged via API
 * with timestamp + user ID (append-only, never overwrite).
 */

const CONSENT_KEY = 'sandvik_mrt_consent';

export default function ConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setIsVisible(true);
    } else {
      setConsent(JSON.parse(stored));
    }
  }, []);

  const saveConsent = async (accepted) => {
    const consentRecord = {
      accepted,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));
    setConsent(consentRecord);
    setIsVisible(false);

    // Log consent to backend (SCD audit trail — append-only)
    try {
      // TODO: Wire to real API endpoint
      // await consentService.logConsentChange({
      //   consent_given: accepted,
      //   timestamp: consentRecord.timestamp,
      // });
      console.log('[ConsentBanner] Consent logged:', consentRecord);
    } catch (err) {
      console.error('[ConsentBanner] Failed to log consent:', err);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-n3 shadow-lg"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="sandvik-container py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-n6 flex-1">
            We use cookies and similar technologies to improve your experience on our site.
            By continuing to browse, you agree to our use of cookies.{' '}
            <a
              href="/privacy"
              className="text-electric-blue hover:underline"
            >
              Learn more
            </a>
          </p>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={() => saveConsent(false)}
              className="px-4 py-2 border border-n3 rounded text-sm font-medium
                         text-carbon-black hover:bg-n1 transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => saveConsent(true)}
              className="px-4 py-2 bg-electric-blue text-white rounded text-sm font-medium
                         hover:bg-blue-dark transition-colors"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * getConsent — utility for other components to check current consent state
 */
export function getConsent() {
  const stored = localStorage.getItem(CONSENT_KEY);
  return stored ? JSON.parse(stored) : null;
}

/**
 * revokeConsent — called from Profile page when user unchecks consent
 * Logs to SCD audit trail and clears stored consent
 */
export async function revokeConsent() {
  const consentRecord = {
    accepted: false,
    timestamp: new Date().toISOString(),
    version: '1.0',
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consentRecord));

  // TODO: Wire to real API endpoint for SCD logging
  console.log('[ConsentBanner] Consent revoked:', consentRecord);
  return consentRecord;
}
